import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { TradingPartnerListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldValues,
  type UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import type { DemandProcurementMasterFormValues } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/models/schema'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { PlanMasterCodesSection } from '~/pages/AdminOcctoMaster/tabs/_plan/components/PlanMasterCodesSection'

type BaseProps = {
  control: Control<DemandProcurementMasterFormValues>
  errors: FieldErrors<DemandProcurementMasterFormValues>
  /** list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。 */
  allBgMasters: BgMasterRow[] | undefined
  /** list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。 */
  submitters: SubmitterMasterRow[] | undefined
  tradingPartners: TradingPartnerListItem[]
  codesErrorMessage: string | undefined
  bgAreaCode: AreaCode | undefined
  selfBgCode: string | undefined
}

type Props =
  | (BaseProps & {
      isEdit: false
      bgs: BgMasterRow[]
      bgCandidates: BgMasterRow[]
      isLoadingFormOptions: boolean
      setValue: UseFormSetValue<DemandProcurementMasterFormValues>
    })
  | (BaseProps & {
      isEdit: true
    })

/**
 * 需要調達計画マスタの Create / Edit 共通 form fields。
 *
 * Create (isEdit=false): bgMasterId select で選択 (validFrom で候補絞り、
 * 失効 BG の fallback ラベル込み)。Edit (isEdit=true): bgCode を readonly 表示。
 * 他 fields (note / TopLevelValidPeriodFields / PlanMasterCodesSection) は共通。
 */
export const DemandProcurementPlanMasterFormFields = (
  props: Props,
): React.ReactElement => {
  const { t } = useTranslation()
  const {
    control,
    errors,
    allBgMasters,
    submitters,
    tradingPartners,
    codesErrorMessage,
    bgAreaCode,
    selfBgCode,
    isEdit,
  } = props
  const idPrefix = isEdit ? 'demandProcMaster-edit' : 'demandProcMaster'

  return (
    <Stack gap={4}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix={idPrefix}
        />
      )}

      {isEdit ? (
        <Controller
          control={control}
          name="bgCode"
          render={({ field }) => (
            <FieldRoot>
              <FieldLabel htmlFor={`${idPrefix}-bgCode`}>
                {t(
                  'page:adminOcctoMaster.demandProcurementPlanMaster.form.bgCode',
                )}
              </FieldLabel>
              <ImmutableInput {...field} id={`${idPrefix}-bgCode`} readOnly />
            </FieldRoot>
          )}
        />
      ) : (
        /* bgMasterId — BG マスタドロップダウン。 単一 select の正は DS `Select`
           (`~/components/common/FormSelect` の JSDoc 参照)。 native の
           `<option value="">…未選択</option>` は placeholder へ移す。 */
        <FieldRoot
          alignItems="stretch"
          invalid={errors.bgMasterId !== undefined}
          required
        >
          <FieldLabel>
            {t(
              'page:adminOcctoMaster.demandProcurementPlanMaster.form.bgMaster',
            )}
            <FieldRequiredIndicator />
          </FieldLabel>
          {props.isLoadingFormOptions ? (
            <Spinner />
          ) : (
            <FormSelect
              aria-label={t(
                'page:adminOcctoMaster.demandProcurementPlanMaster.form.bgMaster',
              )}
              control={control}
              // validFrom 変更で候補外になった選択済み BG を allBgMasters から
              // fallback 解決する。「form value 残置 + 表示空」による silent
              // invalid submission を防ぐ (設計書 §状態マトリクス
              // existing out-of-period value)。 期間外の BG は「表示だけ」で
              // menu には出さない (= 再選択させない)。 submit は handleSave の
              // pre-check が block する。
              fallbackLabel={(v) => {
                const bg = (allBgMasters ?? []).find((b) => b.id === v)
                return bg === undefined
                  ? undefined
                  : `${bg.bgCode}: ${bg.bgName}`
              }}
              name="bgMasterId"
              onValueChange={(selectedId) => {
                const found = props.bgs.find((b) => b.id === selectedId)
                props.setValue('bgCode', found?.bgCode ?? '')
              }}
              // 集約 RPC が小売/自己託送区分でフィルタ済み (area narrow 不要:
              // 代表BG が area を定義する側)。 AC-02: レコードの適用開始日
              // (validFrom) 時点で有効な BG のみ候補に出す。
              options={props.bgCandidates.map((b) => ({
                value: b.id,
                label: `${b.bgCode}: ${b.bgName}`,
              }))}
              placeholder={t(
                'page:adminOcctoMaster.demandProcurementPlanMaster.form.bgMasterUnselected',
              )}
            />
          )}
          {errors.bgMasterId !== undefined && (
            <FieldErrorText>{errors.bgMasterId.message}</FieldErrorText>
          )}
        </FieldRoot>
      )}

      {/* note (任意)。 上限は schema / proto CEL の max_len 100 に揃える
          (design HTML #pm-note は 200 だが実 contract は 100)。 */}
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel htmlFor={`${idPrefix}-note`}>
              {t('page:adminOcctoMaster.demandProcurementPlanMaster.form.note')}
            </FieldLabel>
            <Textarea
              {...field}
              id={`${idPrefix}-note`}
              maxLength={100}
              value={field.value ?? ''}
            />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
            <Text
              as="span"
              textStyle="captionNormal"
              marginTop={1}
              color="Text.midEmphasis"
            >
              {t(
                'page:adminOcctoMaster.demandProcurementPlanMaster.form.noteHint',
              )}
            </Text>
          </FieldRoot>
        )}
      />

      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        idPrefix={idPrefix}
        validFromError={errors.validFrom?.message}
        validToError={errors.validTo?.message}
      />

      <PlanMasterCodesSection
        bgMasters={allBgMasters}
        codesErrorMessage={codesErrorMessage}
        control={control as unknown as Control<FieldValues>}
        externalCodeLabelI18nKey="adminOcctoMaster.demandProcurementPlanMaster.form.externalCodeLabel"
        hideAutoFilledCodeName
        i18nNs="adminOcctoMaster.demandProcurementPlanMaster"
        selfAreaCode={bgAreaCode}
        selfKey={selfBgCode !== '' ? selfBgCode : undefined}
        selfKeyMissingI18nKey="adminOcctoMaster.demandProcurementPlanMaster.form.bgCodeRequired"
        submitters={submitters}
        tradingPartners={tradingPartners}
      />
    </Stack>
  )
}
